import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCertificateTemplates } from "../../redux/slices/certificateTemplateSlice";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

const TIERS = [
  { key: "participation", label: "Participation", desc: "Issued on enrollment" },
  {
    key: "completion",
    label: "Completion",
    desc: "Auto-issued when day threshold met",
  },
  { key: "position_1", label: "1st place", desc: "After course end date" },
  { key: "position_2", label: "2nd place", desc: "After course end date" },
  { key: "position_3", label: "3rd place", desc: "After course end date" },
];

const defaultCerts = () => ({
  participation: { enabled: true, template_id: null },
  completion: { enabled: true, template_id: null, max_missed_days: 0 },
  position_1: { enabled: true, template_id: null },
  position_2: { enabled: true, template_id: null },
  position_3: { enabled: true, template_id: null },
});

function CourseCertificateSettings({ settings, onChange }) {
  const dispatch = useDispatch();
  const { templates } = useSelector((s) => s.certificateTemplate);

  const certs = {
    ...defaultCerts(),
    ...(settings?.certificates || {}),
  };

  useEffect(() => {
    dispatch(fetchCertificateTemplates({ is_active: true }));
  }, [dispatch]);

  const patchTier = (key, patch) => {
    onChange({
      ...settings,
      certificates: {
        ...certs,
        [key]: { ...certs[key], ...patch },
      },
    });
  };

  const templatesForType = (type) =>
    templates.filter((t) => t.type === type && t.is_active);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Certificate awards</CardTitle>
        <CardDescription>
          Participation is issued on enroll. Completion uses the missed-day
          threshold. Position awards run after the course end date. PDFs are
          generated on demand when users download.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {TIERS.map((tier) => (
          <div
            key={tier.key}
            className="border rounded-lg p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{tier.label}</p>
                <p className="text-xs text-muted-foreground">{tier.desc}</p>
              </div>
              <Switch
                checked={!!certs[tier.key]?.enabled}
                onCheckedChange={(v) => patchTier(tier.key, { enabled: v })}
              />
            </div>
            {certs[tier.key]?.enabled && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-xs">Template</Label>
                  <Select
                    value={certs[tier.key]?.template_id || "_default"}
                    onValueChange={(v) =>
                      patchTier(tier.key, {
                        template_id: v === "_default" ? null : v,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Default for type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_default">
                        Default ({tier.label})
                      </SelectItem>
                      {templatesForType(tier.key).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {tier.key === "completion" && (
                  <div className="grid gap-2">
                    <Label className="text-xs">Max missed days allowed</Label>
                    <Input
                      type="number"
                      min={0}
                      value={certs.completion?.max_missed_days ?? 0}
                      onChange={(e) =>
                        patchTier("completion", {
                          max_missed_days: parseInt(e.target.value, 10) || 0,
                        })
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default CourseCertificateSettings;
